import React, { useEffect, useState } from 'react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select'
import { Tablist, TablistItem } from '../../../../../components/ui/tablist'
import { useTranslation } from 'react-i18next'
import { Label } from '../../../../../components/ui/label'
import { Combobox } from '../../../../../components/ui/combobox'
import { Input } from '../../../../../components/ui/input'
import { EqualIcon, Eye, EyeOff, XIcon } from 'lucide-react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../../redux'
import PlannedStopsCalendar from '../../../../../components/portal/stop/PlannedStopsCalendar'
import { DayObject } from '../../../../../components/portal/stop/calendar/Calendar'
import { CONFIG } from '../../../../../config'
import { getCustomerList } from '../../../../../lib/fetch/customer'
import useAuth from '../../../../../hooks/useAuth'
import toast from 'react-hot-toast'
import axios from '../../../../../lib/axios'
import { formatCashAmount } from '../../../../../lib/utils'
import { GenericCoin, GenericNote } from '../../../../../components/elements/'
import { Button } from '../../../../../components/ui/button'
import { z } from 'zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import { convertToDate, formatDateToYYYYMMDD } from '../../../../../lib/date'
import { subMonths } from 'date-fns'

type OrderForeignCurrencyFormProps = {
    defaultValue?: OrderDraft;
    onSuccess?: () => void;
    defaultPartner?: string | null;
    defaultCustomer?: string | null;
}

type CoupureInput = {
    coupure: number,
    qty: number
}

/**
 * Extracts active devies currencies from the notes kassa (cn === "N").
 * Falls back to first active kassa if no notes kassa is found.
 */
function extractDeviesCurrencies(kassaObjects: any[]): DeviesCurrency[] {
    const notesKassa = kassaObjects.find((k: any) => k.cn === 'N' && k.kassaactive)
        || kassaObjects.find((k: any) => k.kassaactive)

    if (!notesKassa) return []

    const currencies = notesKassa.deviescurrency.filter((dc: any) => dc.currencyactive)
    currencies.forEach((dc: any) => {
        dc.devies = dc.devies.filter((d: any) => d.order)
    })

    return currencies
}

const OrderForeignCurrencyForm = ({defaultValue, onSuccess, defaultPartner, defaultCustomer}:OrderForeignCurrencyFormProps) => {
    const { t } = useTranslation()
    const { auth } = useAuth()

    const partners = useSelector((state:RootState)=>state.partners);
    const customers = useSelector((state:RootState)=>state.customers);

    const [view_form, setview_form] = useState<boolean>(true);
    const [selected_subpage, setselected_subpage] = useState<'c' | 'n' | undefined>(undefined);
    const [customers_list, setcustomers_list] = useState<Customer[] | null>();
    
    const [selected_date, setselected_date] = useState<Date | undefined>();
    const [selected_event, setselected_event] = useState<DayObject | null>(null);
    const [selected_partner, setselected_partner] = useState<string | null>(null);
    const [selected_customer, setselected_customer] = useState<string | null>(null);
    const [reference, setreference] = useState<string>('');
    const [selected_currency, setselected_currency] = useState<string | undefined>(undefined);

    const [devies_currencies, setdevies_currencies] = useState<DeviesCurrency[]>([]);
    const [coupure_input, setcoupure_input] = useState<CoupureInput[]>([]);

    useEffect(()=>{
        if(defaultValue){
            setDefaultValues(defaultValue)
        }
    }, [defaultValue])

    useEffect(()=>{
        if(!defaultPartner) return;
        ;(async () => {
            await handlePickPartner(defaultPartner)
            if(defaultCustomer) setselected_customer(defaultCustomer)
        })()
    }, [defaultPartner, defaultCustomer])

    async function setDefaultValues (defaultValue:OrderDraft) {
        try {
            setview_form(true);
            setselected_subpage(defaultValue.cn.toLowerCase() as 'c' | 'n')
            setselected_customer(defaultValue.relnr.toString())
            setreference(defaultValue.reference)

            const deliveryDate = convertToDate(defaultValue.dtdelivery)
            if(deliveryDate){
                setselected_date(deliveryDate)
            }

            if(defaultValue.deviescurrencyid){
                setselected_currency(defaultValue.deviescurrencyid.toString())
            }

            const partner = customers.flatMap(i=>i.eindklant).find(j => j.relnr === defaultValue.relnr)?.partner
            if(partner){
                handlePickPartner(partner.toString())
            }

            let tmp_devies_currencies = [...devies_currencies]
            if(!tmp_devies_currencies || tmp_devies_currencies.length === 0) {
                try {
                    const { data:kassa } = await axios.get(`kassa/get`, {
                        params: {
                            token: auth.data?.token,
                            relnr: partner
                        }
                    })
                    if(kassa.errorcode !== 0){ throw new Error(kassa.message) }
                    if(!Array.isArray(kassa.object)) { console.log('geen deviescurrency ingesteld'); throw new Error() }
                    
                    tmp_devies_currencies = extractDeviesCurrencies(kassa.object)
                    setdevies_currencies(tmp_devies_currencies)
                } catch (err) {
                    console.log(err)
                    toast.error('Something went wrong while getting the foreign currency list', { id:'get-foreign-currency-list' })
                }
            }

            const tmp_coupure = defaultValue.coupure.map(i=>{
                const d = tmp_devies_currencies.find(j=>j.deviescurrencyid.toString() === defaultValue.deviescurrencyid?.toString())?.devies.find(j=>j.value === parseInt(i.coupure))
                return {
                    coupure: parseInt(i.coupure),
                    qty: Math.round(i.qty / parseInt(i.coupure) / (d?.unit || 1))
                }
            }) as CoupureInput[]
            setcoupure_input(tmp_coupure)    
        } catch (err) {
            console.log(err)
        }
    }

    function resetStates () {
        setselected_currency(undefined)
        setcoupure_input([])
        setreference('')
        setselected_date(undefined)
        setselected_event(null)
        setselected_subpage(undefined)
        
        resetSaveOrder()
        resetSaveConcept()
        resetEditConcept()

        onSuccess&&onSuccess()
        refetchDrafts()
    }

    const { refetch:refetchOrders } = useQuery<OrderDraft[] | null>({
        queryKey: ['previous_orders'],
        queryFn: async () => {
            try {
                const {data} = await axios.post(`order/draft/search`, {
                    token: auth.data?.token,
                    startdate: formatDateToYYYYMMDD(subMonths(new Date(), 1)),
                    enddate: formatDateToYYYYMMDD(new Date()),
                    partner: auth.data?.partner,
                    status: '',
                    searchby: 'C'
                })
                if(data.errorcode === 0 && Array.isArray(data.object.orderdraft)) {
                    let res = data.object.orderdraft
                    res = res.filter((i:OrderDraft)=>i.status!=='001')
                    return res
                }
                else { return null; }
            } catch (error) {
                console.log(error)
                return null
            }
        }
    })

    const { refetch:refetchDrafts } = useQuery<null | OrderDraft[]>({
        queryKey: ['order_concept'],
        queryFn: async () => {
            try {
                const {data} = await axios.post(`order/draft/search`, {
                    token: auth.data?.token,
                    partner: auth.data?.partner,
                    status: '001'
                })
                if(data.errorcode === 0 && Array.isArray(data.object.orderdraft)) { return data.object.orderdraft }
                else { return null; }
            } catch (error) {
                console.log(error)
                return null;
            }
        },
        refetchOnWindowFocus: false
    })

    const { mutate:saveOrder, reset:resetSaveOrder } = useMutation({
        mutationFn: async () => {
            try {
                if(!selected_date ||selected_date < new Date()) { toast.error('Date must be in the future', { id:'save-draft' }); return; }
                
                const devies = devies_currencies.find(i=>i.deviescurrencyid.toString() === selected_currency)?.devies.filter(i=>i.cn.toLowerCase() === selected_subpage)
                if(!devies){ toast.error('No devies found', { id:'save-draft' }); return; }
                
                let filtered_coupure_input = coupure_input.filter(i=>i.qty > 0).filter(i=>devies?.find(j=>j.value === i.coupure))
                filtered_coupure_input = filtered_coupure_input.map(i => ({
                    coupure: i.coupure,
                    qty: i.qty * (devies.find(j=>j.value === i.coupure)?.unit || 1)
                }))
                
                let body:any = {
                    valuta: selected_currency,
                    token: auth.data?.token,
                    relnr: selected_customer,
                    cn: selected_subpage,
                    date: formatDateToYYYYMMDD(selected_date),
                    reference: reference,
                    coupures: filtered_coupure_input
                }

                if(defaultValue?.id){ body.id = defaultValue.id }

                toast.loading('Saving draft...', { id:'save-draft' })

                let URL = 'order/draft/create'
                
                if(defaultValue?.id){ URL = 'order/draft/update'; }

                const {data} = await axios.post(URL, body)
                if(data.errorcode !== 0) { throw new Error(data.errorcode) }

                toast.loading('Draft created, processing started...', { id:'save-draft' })

                const role = auth.data?.role || 999
                if(role < 5){
                    await axios.post('order/draft/validate', {
                        token: auth.data?.token,
                        id: data.object.id
                    })
                } else {
                    await axios.post('order/draft/tovalidate', {
                        token: auth.data?.token,
                        orders: data.object.id
                    })
                }

                toast.success('Draft successfully created and processed', { id:'save-draft' })
                resetStates()
                refetchOrders()
            } catch (err) {
                console.log(err)
                toast.error('Something went wrong while saving the order', { id:'save-draft' })
            }
        }
    })

    const { mutate:saveConcept, reset:resetSaveConcept } = useMutation({
        mutationFn: async () => {
            try {
                if(!selected_event) { toast.error('No event selected', { id:'save-concept' }); return; }
                if(selected_event.date < new Date()) { toast.error('Date must be in the future', { id:'save-concept' }); return; }

                const devies = devies_currencies.find(i=>i.deviescurrencyid.toString() === selected_currency)?.devies.filter(i=>i.cn.toLowerCase() === selected_subpage)
                if(!devies){ toast.error('No devies found', { id:'save-draft' }); return; }
                
                let filtered_coupure_input = coupure_input.filter(i=>i.qty > 0).filter(i=>devies?.find(j=>j.value === i.coupure))
                filtered_coupure_input = filtered_coupure_input.map(i => ({
                    coupure: i.coupure,
                    qty: i.qty * (devies.find(j=>j.value === i.coupure)?.unit || 1)
                }))
                
                let body = {
                    valuta: selected_currency,
                    token: auth.data?.token,
                    relnr: selected_customer,
                    cn: selected_subpage,
                    date: formatDateToYYYYMMDD(selected_event.date),
                    reference: reference,
                    coupures: filtered_coupure_input
                }

                toast.loading('Saving draft...', { id:'save-draft' })

                const {data} = await axios.post(`order/draft/create`, body)
                if(data.errorcode !== 0) { throw new Error(data.errorcode) }

                toast.success('Draft created', { id:'save-draft' })
                resetStates()
            } catch (err) {
                console.log(err)
                toast.error('Something went wrong while saving the draft', { id:'save-draft' })
            }
        }
    })

    const { mutate:editConcept, reset:resetEditConcept } = useMutation({
        mutationFn: async () => {
            try {
                if(!selected_date || selected_date < new Date()) { toast.error('Date must be in the future', { id:'save-concept' }); return; }
                if(!defaultValue){ toast.error('No default value found', { id:'save-concept' }); return; }

                const devies = devies_currencies.find(i=>i.deviescurrencyid.toString() === selected_currency)?.devies.filter(i=>i.cn.toLowerCase() === selected_subpage)
                if(!devies){ toast.error('No devies found', { id:'save-draft' }); return; }

                let filtered_coupure_input = coupure_input.filter(i=>i.qty > 0).filter(i=>devies?.find(j=>j.value === i.coupure))
                filtered_coupure_input = filtered_coupure_input.map(i => ({
                    coupure: i.coupure,
                    qty: i.qty * (devies.find(j=>j.value === i.coupure)?.unit || 1)
                }))
                
                let body = {
                    valuta: selected_currency,
                    token: auth.data?.token,
                    relnr: selected_customer,
                    cn: selected_subpage,
                    date: formatDateToYYYYMMDD(selected_date),
                    reference: reference,
                    coupures: filtered_coupure_input,
                    id: defaultValue.id
                }

                toast.loading('Saving draft...', { id:'save-draft' })

                const {data} = await axios.post(`order/draft/update`, body)
                if(data.errorcode !== 0) { throw new Error(data.errorcode) }

                toast.success('Draft updated', { id:'save-draft' })
                resetStates()
            } catch (err) {
                console.log(err)
                toast.error('Something went wrong while saving the draft', { id:'save-draft' })
            }
        }
    })

    useEffect(()=>{
        if (auth.data?.partner && auth.data?.partner.split(",").length === 1) {
            handlePickPartner(auth.data.partner.split(",")[0])
        }
    }, [])

    const handlePickPartner = async (relnr:string) => {
        try {
          if(selected_partner!==relnr){setselected_customer('clear')}
          setselected_partner(relnr);
          setcustomers_list(null);
          setdevies_currencies([]);
  
          const list = await getCustomerList(auth, relnr)
          if(list){ setcustomers_list(list) }
          if(list.length === 1){ setselected_customer(list[0].relnr.toString()) }
        } catch (error) {
          console.log(error)
          toast.error(t("order_and_pickup.o_place_order.toast_wrong"))
        }

        try {
            const { data:kassa } = await axios.get(`kassa/get`, {
                params: {
                    token: auth.data?.token,
                    relnr: relnr
                }
            })
            if(kassa.errorcode !== 0){ throw new Error(kassa.message) }
            if(!Array.isArray(kassa.object)) { console.log('geen deviescurrency ingesteld'); throw new Error() }
            
            const currencies = extractDeviesCurrencies(kassa.object)
            setdevies_currencies(currencies)
        } catch (err) {
            console.log(err)
            toast.error('Something went wrong while getting the foreign currency list', { id:'get-foreign-currency-list' })
        }
    }

    const onPickDate = (v:DayObject) => {
        if(v.cn !== 'CN'){ 
          setselected_subpage(v.cn?.toLowerCase() as 'c' | 'n') 
        } else { 
          setselected_subpage('c') 
        } 
        setselected_event(v)
    }

    function isRequiredReference () {
        if(!selected_partner){ return false }
        const includes = CONFIG.order_and_collect.order.requiredReference_relnrs.includes(selected_partner)
        return includes
    }

    function changeCoupure(coupure:number, value:number) {
        try { 
            let tmp = [...coupure_input]
            
            let v = value;

            const devies = devies_currencies.find(i=>i.deviescurrencyid.toString() === selected_currency)?.devies
            if(!devies){ throw new Error('No devies found') }
            const min = devies.find(i => i.value === coupure)?.min || 0
            const max = devies.find(i => i.value === coupure)?.max || 0

            v = Math.max(min, Math.min(max, v))
            
            const existingIndex = coupure_input.findIndex(item => item.coupure === coupure);

            if(existingIndex !== -1){
                tmp[existingIndex] = {coupure:coupure, qty:v}
                setcoupure_input(tmp)
            }
            else {
                tmp.push({coupure:coupure, qty:v}) 
                setcoupure_input(tmp)
            }
        } catch (err) {
            toast.error('Something went wrong while changing the coupure', { id:'change-coupure' })
        }
    }

    function getCoupureValue(coupure:number) {
        const existingIndex = coupure_input.findIndex(item => item.coupure === coupure);
        if(existingIndex !== -1){
            return coupure_input[existingIndex].qty
        }
        return 0
    }

    function calculateTotal() {
        try { 
            let total = 0;
            const devies = devies_currencies.find(i=>i.deviescurrencyid.toString() === selected_currency)?.devies
            if(!devies){ return total }
            const availableDevies = devies.filter(i=>i.cn.toLowerCase() === selected_subpage)

            coupure_input.forEach(i=>{
                const d = availableDevies.find(j=>j.value === i.coupure)
                if(d){
                    total += i.qty * d.unit * i.coupure
                }
            })
            return total
            
        } catch (err) {
            console.log(err)
            toast.error('Something went wrong while calculating the total', { id:'calculate-total' })
        } 
    }

    function calculateWeight() {
        try { 
            let total = 0;
            const devies = devies_currencies.find(i=>i.deviescurrencyid.toString() === selected_currency)?.devies
            if(!devies){ return total }
            const availableDevies = devies.filter(i=>i.cn.toLowerCase() === selected_subpage)

            coupure_input.forEach(i=>{
                const d = availableDevies.find(j=>j.value === i.coupure)
                if(d){
                    total += i.qty * d.unit * d.weight
                }
            })
            return Math.round(total*1000)/1000
        } catch (err) {
            toast.error('Something went wrong while calculating the total', { id:'calculate-total' })
        } 
    }

    function isComplete() {
        try {
            const s = z.object({
                partner: z.string().min(1),
                customer: z.string().min(1),
                delivery_date: z.date().refine((d) => d > new Date(), {message: 'Date must be in the future'}),
                currency: z.string().min(1),
                coupures: z.array(z.object({
                    coupure: z.number().min(0),
                    qty: z.number().min(0)
                }))
            })

            s.parse({
                partner: selected_partner,
                customer: selected_customer,
                delivery_date: selected_date,
                currency: selected_currency,
                coupures: coupure_input
            })

            const requiredRef = isRequiredReference()
            if(requiredRef){
                const r = z.object({
                    reference: z.string().min(1)
                })
                r.parse({
                    reference: reference
                })
            }

            return true
        } catch (err) {
            return false
        }
    }

    const total = calculateTotal() || 0

    const currencyOptions = Array.isArray(devies_currencies) ? devies_currencies.map(dc=>({value:dc.deviescurrencyid, label:dc.deviescurrencyname})) : []

  return (
    <div className={`flex flex-col ${view_form && 'gap-4'} p-4 shadow-sm border rounded-sm mt-4 md:mt-8`}>

        <div className='flex justify-between'>
            <h2 className='text-lg'>{t("order_and_pickup.o_place_order.subtitle")}</h2>
            <button onClick={()=>{setview_form(prev=>!prev)}} className='flex items-start justify-end w-8 h-8 opacity-50 hover:opacity-100'>{view_form ? <Eye strokeWidth={1.5}/> : <EyeOff strokeWidth={1.5}/>}</button>
        </div>

        <div className={`${view_form ? '' : 'max-h-0'} duration-300 overflow-hidden flex flex-col gap-4`}>
            <div className='grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-4'>
                <div>
                    <Label htmlFor='partner'>{t("order_and_pickup.o_place_order.partner")}</Label>
                    <Combobox disabled={partners?.length === 1} id='partner' selectedValue={selected_partner ? selected_partner : undefined} onValueChange={(value) => { handlePickPartner((value.value).toString()) }} options={Array.isArray(partners) ? partners.map((item) => ({value:item.relnr, label:item.name})) : []} placeholder={t("order_and_pickup.o_place_order.partner_placeholder")||""} input_placeholder={t("order_and_pickup.o_place_order.search_placeholder") || ""}/>
                </div>

                <div>
                    <Label htmlFor='end_customer'>{t("order_and_pickup.o_place_order.customer")}</Label>
                    <Combobox disabled={!Array.isArray(customers_list)} selectedValue={selected_customer ? selected_customer : undefined} id='end_customer' onValueChange={(value) => { setselected_date(undefined); setselected_event(null); setselected_customer(value.value.toString()) }} options={Array.isArray(customers_list) ? customers_list.map((item)=> ({value:item.relnr, label:item.name})) : []} placeholder={t("order_and_pickup.o_place_order.customer_placeholder")||""} input_placeholder={t("order_and_pickup.o_place_order.search_placeholder") || ""}/>
                </div>

                <div>
                    <Label htmlFor='deliverydate'>{t("order_and_pickup.o_place_order.date_delivery")}</Label>
                    <PlannedStopsCalendar page='order' onSelectDay={(v)=>{onPickDate(v)}} disabled={selected_customer==='clear' || !(selected_partner !== null && selected_customer !== null && selected_customer)} partnerid={selected_partner ? parseFloat(selected_partner) : 0} clientrelnr={selected_customer ? parseFloat(selected_customer) : 0} setselectedDate={setselected_date} selectedDate={selected_date}/>
                </div>

                <div>
                    <Label htmlFor='reference' className='flex items-center gap-1 mb-1'>{t("order_and_pickup.o_place_order.reference")}{isRequiredReference() && <span className='text-primary'>*</span>}</Label>
                    <Input value={reference} onChange={({currentTarget})=>{setreference(currentTarget.value)}} id='reference'/>
                </div>

                <div>
                    <Label htmlFor='valuta' className='flex items-center gap-1 mb-1'>{t("order_and_pickup.o_place_order.valuta_label")}</Label>
                    <Select value={selected_currency} onValueChange={(value)=>{setselected_currency(value.toString())}}>
                        <SelectTrigger id='valuta' className='gap-2 font-medium'><SelectValue placeholder={t("order_and_pickup.o_place_order.valuta_placeholder")||""}/></SelectTrigger>
                        <SelectContent>
                            {currencyOptions.map((option,i)=>(
                                <SelectItem key={i} value={option.value.toString()}>{option.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            
            {(selected_subpage && selected_currency) && 
            <Tablist value={selected_subpage} onValueChange={(value)=>{setselected_subpage(value as 'c' | 'n')}}>
                <TablistItem value='c'>{t("order_and_pickup.o_place_order.coins")}</TablistItem>
                <TablistItem value='n'>{t("order_and_pickup.o_place_order.notes")}</TablistItem>
            </Tablist>
            }
            
            {(selected_subpage && selected_currency) &&
            <div className='flex flex-col gap-8'>
                {devies_currencies.find(i=>i.deviescurrencyid.toString() === selected_currency)?.devies
                .filter(d => d.cn.toLowerCase() === selected_subpage)
                .map((d)=>(
                    <div className='flex justify-between gap-4 md:grid md:grid-cols-3' key={`form-${d.value}`}>
                        <div className='flex items-center gap-2'>
                            <div className='hidden min-w-[2.5rem] w-10 h-10 sm:flex justify-center items-center'>
                                {d.cn.toLowerCase() === 'n'
                                    ? <GenericNote value={d.value} currency={devies_currencies.find(i=>i.deviescurrencyid.toString() === selected_currency)?.deviescurrencyname || 'EUR'} className='w-16 h-8'/>
                                    : <GenericCoin value={d.value} currency={devies_currencies.find(i=>i.deviescurrencyid.toString() === selected_currency)?.deviescurrencyname || 'EUR'} className='w-8 h-8'/>}
                            </div>
                            <div>
                                <p className='font-semibold leading-none'>{d.value} {devies_currencies.find(i=>i.deviescurrencyid.toString() === selected_currency)?.deviescurrencyname}</p>
                                <p className='text-xs whitespace-nowrap sm:text-sm text-neutral-500'>{t("order_and_pickup.o_place_order.lias_per")} {d.unit} {t("order_and_pickup.o_place_order.pieces")}</p>    
                            </div>
                        </div>

                        <div className='flex gap-2'>
                            <div className='flex flex-col justify-center items-end min-w-[80px]'>
                                <p className='text-sm leading-none whitespace-nowrap'>min: <span className='font-semibold'>{d.min}</span></p>
                                <p className='text-sm leading-none whitespace-nowrap'>max: <span className='font-semibold'>{d.max}</span></p>
                            </div>
                            <div className='relative px-1 sm:px-3 md:min-w-[100px]'>
                                <Input value={getCoupureValue(d.value)} onChange={({currentTarget})=>{changeCoupure(d.value, Math.round(parseFloat(currentTarget.value) || 0))}} type='number' max={d.max} min={d.min} className='w-full min-w-[100px]'/>
                            </div>
                        </div>

                        <div className='flex items-center justify-between'>
                            <div className='items-center hidden gap-2 md:flex'>
                                <XIcon className='w-4 h-4'/>
                                <p className='font-medium'>{formatCashAmount(d.value * d.unit)}</p>
                                <EqualIcon className='w-4 h-4'/>
                            </div>
                            <p className='font-semibold whitespace-nowrap'>
                                {formatCashAmount(getCoupureValue(d.value) * (d.value * d.unit))}
                                <span className='ml-0.5'>{devies_currencies.find(i=>i.deviescurrencyid.toString() === selected_currency)?.deviescurrencyname}</span>
                            </p>
                        </div>
                    </div>
                ))}
            </div>
            }
            
            {(selected_subpage && selected_currency) &&
            <>
            <div className='w-full h-px bg-neutral-300'></div>

            <div className='flex justify-between font-semibold'>
                <p>{t("order_and_pickup.o_place_order.total")}:</p>
                <div className='flex flex-col items-end'>
                    <p>{formatCashAmount(total)} {devies_currencies.find(i=>i.deviescurrencyid.toString() === selected_currency)?.deviescurrencyname}</p>
                    <p>{calculateWeight()} kg</p>
                </div>
            </div>
            </>
            }

            {(total > 0 ) &&
            <div>
                <Button onClick={()=>{saveOrder()}} disabled={!isComplete()}>{t("order_and_pickup.o_place_order.button")}</Button>
                <Button 
                    onClick={()=>{
                        defaultValue ? editConcept() : saveConcept()
                    }} 
                    disabled={!isComplete()} 
                    variant='link'
                >
                    {t("order_and_pickup.o_place_order.button_concept")}
                </Button>
            </div>
            }
        </div>
    </div>
  )
}

export default OrderForeignCurrencyForm